import { useState, type FormEvent } from "react";
import {
  Eye,
  EyeOff,
  ShieldCheck,
  ArrowRight,
  Building2,
  Crown,
  Zap,
  Radio,
  QrCode,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { ApiError, login } from "../lib/api";

type LoginPageProps = {
  onAuthenticated: (token: string) => void;
};

export function LoginPage({ onAuthenticated }: LoginPageProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(undefined);
    try {
      const result = await login(username, password);
      onAuthenticated(result.access_token);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Invalid username or password");
    } finally {
      setSubmitting(false);
    }
  }

  function fillSuperAdmin() {
    setUsername("admin");
    setPassword("AdminSecurePass2026!");
  }

  function fillTenant() {
    setUsername("tenant_demo");
    setPassword("TenantSecurePass2026!");
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4 lg:p-10 selection:bg-emerald-500 selection:text-white">
      {/* Main Container */}
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        
        {/* Left Hero & Feature Showcase (Clean eNotify Light Theme) */}
        <div className="hidden lg:flex lg:col-span-6 flex-col justify-between space-y-6 pr-4">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              <span>WhatsApp Multi-Channel Infrastructure</span>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center font-black text-white text-xl shadow-sm">
                WA
              </div>
              <div>
                <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">WA Master</h1>
                <p className="text-xs text-slate-500">Enterprise High-Throughput WhatsApp Platform</p>
              </div>
            </div>

            <p className="text-slate-600 text-sm leading-relaxed">
              Manage your business conversations with <strong>WhatsApp QR linking</strong>, a shared inbox and instance-scoped APIs.
            </p>
          </div>

          {/* Feature Highlights Grid */}
          <div className="grid grid-cols-2 gap-3.5">
            <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-2xs space-y-1">
              <div className="flex items-center gap-2 text-blue-600 font-bold text-xs">
                <Radio className="w-4 h-4" /> Connected Inbox
              </div>
              <p className="text-[11px] text-slate-500 leading-normal">
                Conversations, message delivery updates and reusable replies.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-2xs space-y-1">
              <div className="flex items-center gap-2 text-emerald-600 font-bold text-xs">
                <QrCode className="w-4 h-4" /> Direct QR Instances
              </div>
              <p className="text-[11px] text-slate-500 leading-normal">
                Multi-device WebSockets with hardware-accelerated encryption.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-2xs space-y-1">
              <div className="flex items-center gap-2 text-purple-600 font-bold text-xs">
                <Zap className="w-4 h-4" /> Anti-Ban Pacing Guard
              </div>
              <p className="text-[11px] text-slate-500 leading-normal">
                Adaptive token-bucket throttler protecting phone numbers.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-2xs space-y-1">
              <div className="flex items-center gap-2 text-amber-600 font-bold text-xs">
                <ShieldCheck className="w-4 h-4" /> Financial Ledger
              </div>
              <p className="text-[11px] text-slate-500 leading-normal">
                Immutable double-entry credit accounting with maker-checker approvals.
              </p>
            </div>
          </div>

          {/* Compliance & SLA Footer */}
          <div className="flex items-center gap-4 text-xs text-slate-500 pt-2 border-t border-slate-200">
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> 99.99% Core Uptime</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Zero Gateway Dependency</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> SOC2 Audited</span>
          </div>
        </div>

        {/* Right Authentication Card (Pristine Clean White) */}
        <div className="lg:col-span-6 w-full max-w-md mx-auto">
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm relative">
            
            {/* Mobile Brand */}
            <div className="lg:hidden text-center mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center font-black text-white text-xl mx-auto mb-2 shadow-sm">
                WA
              </div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">WA Master</h1>
              <p className="text-slate-500 text-xs mt-0.5">Multi-Channel WhatsApp Platform</p>
            </div>

            <div className="mb-6">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-2">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Single Sign-On Gate</span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 tracking-tight">Sign In to WA Master</h2>
              <p className="text-slate-500 text-xs mt-1">
                Enter your credentials to access the console.
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={(event) => void submit(event)} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Username or Email
                </label>
                <input
                  name="username"
                  autoComplete="username"
                  placeholder="admin or username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  required
                  autoFocus
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-slate-900 text-xs focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 bg-white placeholder:text-slate-400 shadow-2xs"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-xs font-semibold text-slate-700">Password</label>
                </div>
                <div className="relative">
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    className="w-full pl-3.5 pr-10 py-2.5 rounded-lg border border-slate-300 text-slate-900 text-xs focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 bg-white placeholder:text-slate-400 shadow-2xs"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 transition-colors p-1"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full py-2.5 text-xs font-bold flex items-center justify-center gap-2 mt-2 shadow-sm"
              >
                <span>{submitting ? "Authenticating..." : "Sign In to Workspace"}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            {/* 1-Click Role Fill Chips (Clean Pastel Buttons) */}
            <div className="mt-6 pt-5 border-t border-slate-100">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400 text-center mb-2.5">
                Quick Test Credentials
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={fillSuperAdmin}
                  className="p-2.5 rounded-lg bg-purple-50/70 border border-purple-200/80 hover:bg-purple-100/80 transition-all text-left flex items-center gap-2.5 cursor-pointer"
                >
                  <div className="p-1 rounded bg-purple-100 text-purple-700">
                    <Crown className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold text-purple-900">Super Admin</div>
                    <div className="text-[10px] font-mono text-purple-600">admin</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={fillTenant}
                  className="p-2.5 rounded-lg bg-emerald-50/70 border border-emerald-200/80 hover:bg-emerald-100/80 transition-all text-left flex items-center gap-2.5 cursor-pointer"
                >
                  <div className="p-1 rounded bg-emerald-100 text-emerald-700">
                    <Building2 className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold text-emerald-900">Tenant User</div>
                    <div className="text-[10px] font-mono text-emerald-600">tenant_demo</div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <p className="text-center text-[11px] text-slate-400 mt-5">
            WA Master v1.1 · Enterprise WhatsApp Platform
          </p>
        </div>
      </div>
    </div>
  );
}
